/**
 * Team Settings → Support (FR-HELP-07, AC-ARCH-REVIEW-004).
 *
 * Where support requests from this team's members are routed. Blank = the
 * platform default (`SUPPORT_RECIPIENT_EMAIL_DEFAULT` on the help service), so
 * the current value is always stated explicitly rather than left ambiguous.
 */
import { useState } from 'react';

import { useToast } from '../../app/toast-context';
import { ConfirmDialog } from '../../components/confirm-dialog';
import { DeskSection } from '../../components/desk-section';
import { UnsavedChangesGuard } from '../../components/unsaved-changes-guard';
import { ApiError } from '../../lib/api-error';
import type { TenantSummary } from '../../lib/api-types';

/** Pragmatic client-side check; the tenant service is the authority. */
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

export interface SupportEmailSettingsProps {
  tenant: TenantSummary | null;
  isLoading: boolean;
  error: string | null;
  onRetry: () => void;
  onSave: (patch: { support_recipient_email: string | null }) => Promise<void>;
}

export function SupportEmailSettings({
  tenant,
  isLoading,
  error,
  onRetry,
  onSave,
}: SupportEmailSettingsProps): JSX.Element {
  const { showToast } = useToast();
  const [draft, setDraft] = useState<string | null>(null);
  const [fieldError, setFieldError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isClearing, setIsClearing] = useState(false);
  const [dialogError, setDialogError] = useState<string | null>(null);

  if (tenant === null) {
    if (isLoading) {
      return (
        <p role="status" className="muted">
          Loading support settings…
        </p>
      );
    }
    return (
      <div className="banner banner--error" role="alert">
        <p>{error ?? 'Support settings could not be loaded.'}</p>
        <button type="button" className="btn btn--secondary btn--sm" onClick={onRetry}>
          Try again
        </button>
      </div>
    );
  }

  const current = tenant.support_recipient_email ?? null;
  const value = draft ?? current ?? '';

  async function submit(): Promise<void> {
    const trimmed = value.trim();
    if (trimmed !== '' && !EMAIL_PATTERN.test(trimmed)) {
      setFieldError('Enter a valid email address, for example coach@school.org.');
      return;
    }
    setFieldError(null);
    setIsSaving(true);
    try {
      await onSave({ support_recipient_email: trimmed === '' ? null : trimmed });
      setDraft(null);
      showToast(
        trimmed === '' ? 'Support email reset to the platform default.' : 'Support email updated.',
        'success'
      );
    } catch (caught) {
      setFieldError(
        ApiError.isApiError(caught)
          ? caught.message
          : 'The support email could not be saved. Please try again.'
      );
    } finally {
      setIsSaving(false);
    }
  }

  async function clearOverride(): Promise<void> {
    setDialogError(null);
    try {
      await onSave({ support_recipient_email: null });
      setDraft(null);
      setIsClearing(false);
      showToast('Support email reset to the platform default.', 'success');
    } catch (caught) {
      setDialogError(
        ApiError.isApiError(caught)
          ? caught.message
          : 'The support email could not be reset. Please try again.'
      );
    }
  }

  return (
    <div className="team-settings__panel">
      <UnsavedChangesGuard isDirty={draft !== null && draft !== (current ?? '')} />
      <DeskSection
        title="Support routing"
        description="Support requests from your team members go to this address. Leave it blank to use the platform support desk."
      >
        <p className="team-settings__current" data-testid="support-email-current">
          {current === null ? (
            <>
              Currently using the <strong>platform default</strong> support desk.
            </>
          ) : (
            <>
              Currently routed to <strong>{current}</strong>.
            </>
          )}
        </p>

        <form
          className="team-settings__form desk-form"
          onSubmit={(event) => {
            event.preventDefault();
            void submit();
          }}
          noValidate
        >
          <div className={`field${fieldError === null ? '' : ' field--invalid'}`}>
            <label className="field__label" htmlFor="support-email">
              Support recipient email
            </label>
            <input
              id="support-email"
              className="field__control"
              type="email"
              inputMode="email"
              autoComplete="email"
              placeholder="athletics@school.org"
              value={value}
              onChange={(event) => setDraft(event.target.value)}
              aria-invalid={fieldError === null ? undefined : true}
              aria-describedby={fieldError === null ? undefined : 'support-email-error'}
            />
            {fieldError === null ? null : (
              <span className="field__error" id="support-email-error" role="alert">
                {fieldError}
              </span>
            )}
          </div>
          <div className="desk-form__actions desk-form__full">
            <button type="submit" className="btn btn--primary" disabled={isSaving}>
              {isSaving ? 'Saving…' : 'Save support email'}
            </button>
            {current === null ? null : (
              <button
                type="button"
                className="btn btn--secondary"
                onClick={() => setIsClearing(true)}
              >
                Use platform default
              </button>
            )}
          </div>
        </form>
      </DeskSection>

      {isClearing ? (
        <ConfirmDialog
          title="Use the platform default?"
          consequence="New support requests from your team will go to the Gridiron Tracker support desk instead of your own address. Requests already sent are unaffected."
          confirmLabel="Use platform default"
          error={dialogError}
          onConfirm={() => void clearOverride()}
          onCancel={() => {
            setIsClearing(false);
            setDialogError(null);
          }}
        />
      ) : null}
    </div>
  );
}
